import {
  toIso,
  countryName,
  formatMoney,
  addAgendaItem,
  getLedger,
  getNearby,
  getPlan,
  DayStep,
  NotFound,
  orderAgenda,
  PencilIcon,
  reorderStops,
  updateStop,
  type Point,
  type ShareStation,
  type Spot,
  type DayLeg,
  type Expense,
  type NearThing,
  type Plan,
  type PlanStop,
  type WaySaid,
} from '@trippath/shared';
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

import { AgendaDialog } from './AgendaDialog';
import { gpsSpot, SpotField } from '../../components/place/SpotField';
import { DayWayDialog } from './DayWayDialog';
import { keepWay, legOf, resolve, segmentsOf, TRANSIT, type Segment } from './dayWays';
import { RideLegs } from '../../components/way/RideLegs';
import { dayItems, stepsOf, stopDays, type DayItem, type Step } from './cityDays';
import { RideChoices } from './RideChoices';
import { instantOf, minutesSaid, wallOf } from '../../lib/clock';
import { FileView, type Viewed } from '../../components/file/FileView';
import { priceSaid } from '../../lib/format';
import { withDays, withWayAt } from '../../lib/stopInput';
import { layoutOf } from '../../components/timeline/timeline';
import { Timeline, type AgendaBlock } from '../../components/timeline/Timeline';
import { dayRange, plusDays } from '../../lib/countryFacts';
import { DockFrame } from '../../components/widget/Dock';
import { MapPanel } from '../../components/map/MapPanel';
import { ModeIcon } from '../../components/icons/ModeIcon';
import { PlacePhoto } from '../../components/place/PlacePhoto';
import { rideKey, roadKey, useRides, useRoads, type RideAsk, type Way } from '../../lib/roads';
import type { MapData, MapLine, MapPoint } from '../../lib/map';
import type { MapPoi } from '../../components/map/TripMap';
import { WayDetail } from '../../components/way/WayDetail';
import { useShown, Widget, WidgetDock } from '../../components/widget/Widget';
import { lengthOf, minutesOf, shortest, type Pace } from '../../lib/dayOrder';
import { useLive } from '../../app/live';
import { readyWeather, useWeather } from '../../components/weather/useWeather';
import { WaitingPlaces } from './WaitingPlaces';
import { useReadOnly } from '../../app/readOnly';

/** An hour and a day, in milliseconds. */
const HOUR = 3_600_000;
const DAY = 24 * HOUR;

/** Where the widgets start over the map, as the plan's do; each person moves them. */
const LEFT = { left: 16, top: 96 };
const RIGHT = { right: 16, top: 16 };
const BOTTOM = { left: 352, bottom: 64, top: 16 };
/** What the widgets cover of the map's edges, kept free when it is fitted. */
const COVERED = { top: 80, right: 330, bottom: 220, left: 350 };
/** The room between the days on the left and the thing picked on the right. */
const between = (area: number) => Math.max(320, area - 352 - 332);
/** What shows on a first visit; the dock opens and closes each. */
const START = { 'city-days': true, 'city-picked': true, 'city-hours': true };

type State =
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'failed' }
  | { kind: 'ready'; plan: Plan };

/** A length as a way says it: `450 m`, `1.8 km`. */
export function distanceSaid(metres: number, lang: string): string {
  return metres < 1000
    ? `${String(Math.round(metres / 10) * 10)} m`
    : `${(metres / 1000).toLocaleString(lang, { maximumFractionDigits: 1 })} km`;
}

/**
 * A way to a thing as the list says it, beside its mark: how far and how long — `1.8 km · 24
 * min` — the length about, `≈ 1.6 km`, when it is as the crow flies, its time when said.
 */
export function waySaid(
  lang: string,
  metres: number,
  minutes: number | null,
  straight: boolean,
): string {
  const distance = `${straight ? '≈ ' : ''}${distanceSaid(metres, lang)}`;
  return minutes === null ? distance : `${distance} · ${minutesSaid(minutes)}`;
}

/** A place's pin, as a map marks an address. */
function PinIcon() {
  return (
    <svg
      className="mode-icon"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0ZM12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
    </svg>
  );
}

/** What a step goes to, by its name: a thing, a room, the station of the way on. */
function endName({ to, end }: Step): string {
  if (to.kind === 'thing') return to.item.name;
  if (to.kind === 'stay' || to.kind === 'back') return to.booking.name;
  return end.name;
}

/** Minutes in a day: a thing's hours that pass midnight end the next one. */
const DAY_MINUTES = 1440;

/** A thing of the day with a card of its own: all but the way back to the room. */
type Card = Exclude<DayItem, { kind: 'back' }>;

/** A thing of the day on the map: a room as a house, a thing by its number, a way's end quiet. */
function pointOf(item: DayItem): MapPoint[] {
  if (!item.place) return [];
  const { lat, lon } = item.place;
  const { key } = item;
  // Back to the room: drawn once, as the morning's.
  if (item.kind === 'back') return [];
  if (item.kind === 'stay') {
    return [
      {
        lat,
        lon,
        key,
        label: item.booking.name,
        kind: 'stay',
        mark: item.booking.lodging ?? 'stay',
      },
    ];
  }
  if (item.kind === 'thing') {
    return [{ lat, lon, key, label: `${String(item.number)} ${item.item.name}`, kind: 'stop' }];
  }
  return [{ lat, lon, key, label: item.place.name, kind: 'end' }];
}

/**
 * A stop's days from inside, the trip's second level: each day what it holds — where one slept,
 * comes in, does, leaves and sleeps — and the walks between; on the map, the thing picked beside
 * it, and the day by the hour.
 */
/** How high a pressed place's card is: one pressed nearer the map's top has it below. */
const POI_ROOM = 150;
/** How wide the card is, and the map's buttons at its right. */
const POI_WIDE = 300;

/** Where a pressed place's card stands: at the point, its arrow on it, inside the map. */
function poiPlace(poi: { x: number; y: number; room: number }): CSSProperties {
  const left = Math.max(8 + 34, Math.min(poi.x, poi.room - POI_WIDE - 56 + 34));
  return {
    left,
    top: poi.y,
    ['--pop-arrow' as string]: `${String(poi.x - left + 26)}px`,
  };
}

/** The day's shortest order against its order now (the `DayOrder` mockup): how far and how long
 * each way, by foot, its transport or a car, kept or let go. */
function DayOrderPanel({
  now,
  best,
  pace,
  onPace,
  onCancel,
  onApply,
}: {
  now: number;
  best: number;
  pace: Pace;
  onPace: (pace: Pace) => void;
  onCancel: () => void;
  onApply: () => void;
}) {
  const { t, i18n } = useTranslation();
  const km = (value: number) =>
    t('dayOrder.km', {
      km: value.toLocaleString(i18n.language, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }),
    });
  const time = (value: number) => {
    const minutes = minutesOf(value, pace);
    return t('dayOrder.time', { h: Math.floor(minutes / 60), m: minutes % 60 });
  };
  const same = best >= now - 0.05;
  return (
    <section className="day-order" aria-labelledby="day-order-title">
      <p id="day-order-title" className="day-order-title">
        {t('dayOrder.title')}
      </p>
      <div className="segmented" role="group" aria-label={t('dayOrder.pace')}>
        {(['walk', 'transit', 'car'] as const).map((one) => (
          <button
            key={one}
            type="button"
            aria-pressed={one === pace}
            onClick={() => {
              onPace(one);
            }}
          >
            {t(`dayOrder.by.${one}`)}
          </button>
        ))}
      </div>
      <div className="day-order-compare">
        <div>
          <span>{t('dayOrder.now')}</span>
          <b>{km(now)}</b>
          <span>{time(now)}</span>
        </div>
        <span aria-hidden="true">→</span>
        <div className="day-order-new">
          <span>{t('dayOrder.new')}</span>
          <b>{km(best)}</b>
          <span>{time(best)}</span>
        </div>
      </div>
      {same && <p className="quiet">{t('dayOrder.already')}</p>}
      <p className="quiet day-order-note">{t('dayOrder.note')}</p>
      <div className="dialog-actions">
        <button type="button" className="button" onClick={onCancel}>
          {t('dayOrder.cancel')}
        </button>
        {!same && (
          <button type="button" className="button button-primary" onClick={onApply}>
            {t('dayOrder.apply')}
          </button>
        )}
      </div>
    </section>
  );
}

export function CityDays() {
  const { t, i18n } = useTranslation();
  // A trip read only: its days looked at, nothing added, moved or changed.
  const locked = useReadOnly();
  const lang = i18n.language;
  const { id = '', stop: stopId = '' } = useParams();
  // The weather of its days, by their parts near, under its plan.
  const sky = useWeather(id);
  // Read again when someone else changes the trip.
  const { version: changed } = useLive();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [picked, setPicked] = useState<string | null>(null);
  const [day, setDay] = useState<string | null>(null);
  // A thing being changed, or a new one, maybe at a place found or pressed on the map.
  const [editing, setEditing] = useState<{ item: string | null; at?: Spot } | null>(null);
  // A thing's photo or PDF looked at in place.
  const [looking, setLooking] = useState<Viewed | null>(null);
  // A day's own way being said: which, from where, to where.
  const [dayWayOpen, setDayWayOpen] = useState<{
    leg: DayLeg;
    from: string;
    to: string;
  } | null>(null);
  // Laid out as the plan is: its widgets over the map, each moved, docked and closed as one likes.
  const [canvas, setCanvas] = useState<HTMLDivElement | null>(null);
  const [failed, setFailed] = useState(false);
  // A thing of the day held and dragged, and the one it is over.
  const [dragged, setDragged] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const navigate = useNavigate();
  const [widgets, show] = useShown(START);
  // A day's things in their shortest order, looked at before it is kept, and how one goes.
  const [ordering, setOrdering] = useState<{ day: string; order: string[]; pace: Pace } | null>(
    null,
  );
  // A way picked: the bike stations near it, and where one is when asked, on the map.
  const [docks, setDocks] = useState<ShareStation[]>([]);
  const [me, setMe] = useState<Point | null>(null);
  // A station or a sight pressed on the map: to add to the day looked at.
  const [poi, setPoi] = useState<
    (MapPoi & { room: number; near: NearThing[] | null; ask: number }) | null
  >(null);
  const asked = useRef(0);
  const mapBox = useRef<HTMLElement>(document.createElement('section'));

  useEffect(() => {
    getPlan(id).then(
      (plan) => {
        setState({ kind: 'ready', plan });
      },
      (error: unknown) => {
        setState({ kind: error instanceof NotFound ? 'missing' : 'failed' });
      },
    );
  }, [id, changed]);
  // The trip's shared costs, each said on its day; none where they cannot be read.
  const [costs, setCosts] = useState<Expense[]>([]);
  useEffect(() => {
    getLedger(id).then(
      (ledger) => {
        setCosts(ledger.expenses.filter((expense) => !expense.settlement));
      },
      () => {
        setCosts([]);
      },
    );
  }, [id, changed]);

  const plan = state.kind === 'ready' ? state.plan : null;
  const stop = plan?.stops.find((one) => one.id === stopId) ?? null;
  const all = stop ? stopDays(stop) : [];
  // Today by the clock here, as a cost paid today is kept.
  const today = toIso(new Date());
  const shown = day ?? (all.includes(today) ? today : (all[0] ?? ''));
  // Every day of the stop with what it holds: the timeline shows them all, the list one.
  const plain = plan && stop ? all.map((on) => ({ on, items: dayItems(plan, stop, on) })) : [];
  const base = plain.find(({ on }) => on === shown)?.items ?? [];
  // The day's things, which keep their block among its other items; ordered as looked at.
  const firstThing = base.findIndex((item) => item.kind === 'thing');
  const looked = ordering && ordering.day === shown ? ordering.order : null;
  const items = looked
    ? base
        .map((item, at) => ({
          item,
          at: item.kind === 'thing' ? firstThing + looked.indexOf(item.key) : at,
        }))
        .sort((a, b) => a.at - b.at)
        .map(({ item }) => item)
    : base;
  // Where the day starts and ends: the last place before its things, the first after them.
  const halves = firstThing < 0 ? [] : [base.slice(0, firstThing), base.slice(firstThing)];
  const dayStart =
    halves[0]?.findLast((item) => item.kind !== 'thing' && item.place)?.place ?? null;
  const dayEnd =
    halves[1]?.find((item) => item.kind !== 'thing' && item.place)?.place ?? dayStart;
  const spots = base.flatMap((item) =>
    item.kind === 'thing'
      ? [{ key: item.key, point: item.place, fixed: item.at !== null }]
      : [],
  );
  const movable = spots.filter((spot) => !spot.fixed && spot.point !== null).length;
  // The days as drawn and walked: the day looked at in its new order.
  const held = plain.map((one) => (one.on === shown ? { on: one.on, items } : one));
  const dayWays = stop ? stop.dayWays : [];
  // How each step goes, as said: by the thing it goes to, or as the day's own way.
  const saidOf = (step: Step, on: string): WaySaid | null =>
    step.to.kind === 'thing'
      ? step.to.item
      : (dayWays.find((way) => way.day === on && way.leg === legOf(step)) ?? null);
  // When a way sets out, on the stop's clock: as the thing before it ends, else half an hour
  // before the one it goes to, else at ten.
  const zone = stop ? stop.zone : null;
  const leavesAt = (step: Step, on: string) => {
    const at = (clock: string, minutes: number) =>
      new Date(Date.parse(instantOf(on, clock, zone)) + minutes * 60_000).toISOString();
    const { from, to } = step;
    if (from.kind === 'thing' && from.at !== null) {
      return from.item.ends ? at(from.item.ends, 0) : at(from.at, 60);
    }
    return to.kind === 'thing' && to.at !== null ? at(to.at, -30) : at('10:00', 0);
  };
  // Each step in its parts, each found as it goes: a walk along the streets, a ride on the
  // town's transport by its timetables — its lines, its minutes, the walks to and from its stops.
  const stepped = held.flatMap(({ on, items: those }) =>
    stepsOf(those).map((step) => ({
      on,
      step,
      segments: segmentsOf(step, saidOf(step, on), endName(step)),
    })),
  );
  const rideAsk = (step: Step, segment: Segment, on: string): RideAsk => ({
    from: segment.from,
    to: segment.to,
    at: leavesAt(step, on),
  });
  const roads = useRoads(
    stepped.flatMap(({ segments }) =>
      segments
        .filter((segment) => segment.by === 'walk')
        .map((segment): Way => ({
          from: segment.from,
          to: segment.to,
          sea: false,
          foot: true,
        })),
    ),
  );
  const ridden = useRides(
    stepped.flatMap(({ on, step, segments }) =>
      segments
        .filter((segment) => TRANSIT.has(segment.by))
        .map((segment) => rideAsk(step, segment, on)),
    ),
  );
  const routes = stepped.map(({ on, step, segments }) => ({
    on,
    step,
    first: segments[0],
    parts: segments.map((segment) => {
      const ask = rideAsk(step, segment, on);
      const walk =
        segment.by === 'walk' ? roads.get(roadKey(ask.from, ask.to, false, true)) : null;
      const ride = TRANSIT.has(segment.by)
        ? ridden.get(rideKey(ask.from, ask.to, ask.at))
        : null;
      return { segment, found: resolve(segment, walk ?? null, ride ?? null) };
    }),
  }));
  // Each way as the list and the timeline say it: its marks, how far and how long, in words —
  // its minutes when each part's are known.
  const moveOf = ({ first, parts }: (typeof routes)[number]) => {
    const each = parts.flatMap(({ found }) => (found.minutes === null ? [] : [found.minutes]));
    const minutes =
      each.length === parts.length ? each.reduce((all, one) => all + one, 0) : null;
    const metres = parts.reduce((all, { found }) => all + found.metres, 0);
    const rough = parts.some(({ found }) => found.rough);
    return {
      mode: first.by,
      modes: parts.map(({ segment }) => segment.by),
      minutes,
      said: waySaid(lang, metres, minutes, rough),
    };
  };
  const moveTo = (item: DayItem, day = shown) => {
    const route = routes.find(({ on, step }) => on === day && step.to.key === item.key);
    return route ? moveOf(route) : null;
  };
  // The day looked at: its own ways, on the map and in the list.
  const todays = routes.filter(({ on }) => on === shown);
  // The way on from a thing of the day: where to, how and how long.
  const onFrom = (item: DayItem) => {
    const route = todays.find(({ step }) => step.from.key === item.key);
    return route ? { name: endName(route.step), move: moveOf(route) } : null;
  };
  // The way to a thing of the day: where it sets out from, how and how long.
  const hereOf = (item: DayItem) => {
    const route = todays.find(({ step }) => step.to.key === item.key);
    return route && { from: nameOf(route.step.from), move: moveOf(route) };
  };
  // What the day walks along the streets: its walks' metres.
  const walkedMetres = todays
    .flatMap(({ parts }) => parts)
    .filter(({ segment, found }) => segment.by === 'walk' && !found.rough)
    .reduce((all, { found }) => all + found.metres, 0);
  const data: MapData = {
    points: [
      ...items.flatMap(pointOf),
      ...docks.map((dock): MapPoint => ({
        lat: dock.lat,
        lon: dock.lon,
        label: dock.bikes === null ? dock.name : `${dock.name} · ${String(dock.bikes)}`,
        kind: 'dock',
        ...(dock.bikes === null ? {} : { mark: String(dock.bikes) }),
      })),
      ...(me ? [{ ...me, label: t('rides.you'), kind: 'fix' as const }] : []),
    ],
    lines: todays.flatMap(({ step, parts }) =>
      parts.flatMap(({ found }, at) =>
        found.lines.map((line): MapLine => ({
          ...line,
          stop: step.to.key,
          part: `${step.to.key}#${String(at)}`,
        })),
      ),
    ),
  };

  if (state.kind === 'loading') return <p className="page quiet">{t('trips.loading')}</p>;
  if (state.kind !== 'ready' || !stop) {
    return (
      <div className="page">
        <p className="form-failed" role="alert">
          {t(
            state.kind === 'ready'
              ? 'city.missing'
              : state.kind === 'missing'
                ? 'trips.notFound'
                : 'trips.failed',
          )}
        </p>
        <Link to={state.kind === 'ready' ? `/trips/${id}` : '/'}>{t('trips.back')}</Link>
      </div>
    );
  }

  const { plan: ready } = state;
  // What can be picked: all but the way back to the room, which has no card of its own.
  const chosen =
    items.find((item): item is Card => item.key === picked && item.kind !== 'back') ?? null;
  // A way picked: the step to the thing it goes to, shown on the map as the plan shows a way.
  // A way picked, or one part of it: `way:Palace`, `way:Palace#1`.
  const [wayTo, partPicked] = (picked?.startsWith('way:') ? picked.slice(4) : '').split('#');
  const shownRoute = todays.find(({ step }) => step.to.key === wayTo) ?? null;
  const shownStep = shownRoute ? shownRoute.step : null;
  const shownPart =
    shownRoute && partPicked !== undefined ? shownRoute.parts[Number(partPicked)] : undefined;
  const editingItem = stop.agenda.find((one) => one.id === editing?.item);
  const saved = (next: Plan) => {
    setEditing(null);
    setDayWayOpen(null);
    setState({ kind: 'ready', plan: next });
  };
  // A day planned, marked in the calendar of the days: how many things it holds.
  const planned = (one: string) => {
    const count = stop.agenda.filter((thing) => thing.day === one).length;
    return count > 0 ? { mark: '•', said: t('city.planned', { count }) } : null;
  };
  // A thing of the day picked, from the list, the map or the timeline: said beside the map.
  const pick = (key: string) => {
    setPicked(key);
    show('city-picked', true);
  };
  // A thing by its name: a room back at the day's end as at its start.
  const nameOf = (item: DayItem) => (item.kind === 'back' ? item.booking.name : what(item));
  // What the pencil changes: the thing picked, the thing a way picked goes to, or the way back
  // to the room.
  const editable =
    shownStep?.to.kind === 'thing'
      ? shownStep.to.item
      : chosen?.kind === 'thing'
        ? chosen.item
        : null;
  // A day's own way picked: said in its own dialog.
  const leg = shownStep ? legOf(shownStep) : null;
  // The day in the town, on its own clock: its stations' bikes are said only for today.
  const townToday = wallOf(new Date().toISOString(), stop.zone).day;
  // Come to by car: car parks to leave it at and ride on from.
  const cameByCar =
    ready.stops[ready.stops.findIndex((one) => one.id === stop.id) - 1]?.out?.mode === 'car';
  // The way to a thing in the list: its mark, how far and how long, red when not made in time;
  // pressed, picked and shown on the map, as the plan's ways are.
  const wayLine = (item: DayItem, move: { mode: string; modes: string[]; said: string }) => {
    const key = `way:${item.key}`;
    const late = lates.includes(item.key);
    return (
      <button
        type="button"
        className={`city-walk city-by-${move.mode}${late ? ' city-late' : ''}`}
        aria-pressed={picked === key || picked?.startsWith(`${key}#`) === true}
        onClick={() => {
          if (picked === key) setPicked(null);
          else pick(key);
        }}
      >
        {move.modes.map((mode, at) => (
          <ModeIcon key={`${String(at)} ${mode}`} mode={mode} size={12} />
        ))}
        <span className="visually-hidden">
          {move.modes.map((mode) => t(`city.by.${mode}`)).join(' → ')}{' '}
        </span>
        {move.said}
        {late && <span className="city-late-said"> · {t('city.late')}</span>}
      </button>
    );
  };
  // A change made on the timeline: the plan as it is then, or said it was not kept.
  const change = (asked: Promise<Plan>) => {
    setFailed(false);
    asked.then(saved, () => {
      setFailed(true);
    });
  };
  // A place pressed on the map, or near it, added to the day looked at.
  const addThing = (name: string, at: Point) => {
    setPoi(null);
    change(
      addAgendaItem(ready.trip.id, {
        stop: stop.id,
        name,
        day: shown,
        starts: null,
        ends: null,
        place: { lat: at.lat, lon: at.lon, name },
        price: null,
        currency: ready.trip.currency,
        booked: false,
        link: null,
        by: null,
        byMinutes: null,
        parts: [],
      }),
    );
  };
  // The day's things in their order, moved one from a place to another; to nowhere, not moved.
  const order = items.flatMap((item) => (item.kind === 'thing' ? [item.key] : []));
  const moveThing = (from: number, to: number) => {
    const moving = order[from];
    // A trip read only: the day's things stay in their order.
    if (locked || moving === undefined || to < 0 || to >= order.length || from === to) return;
    change(orderAgenda(ready.trip.id, order.toSpliced(from, 1).toSpliced(to, 0, moving)));
  };
  // Another stop opened on the timeline: its own days, from inside.
  const go = (at: PlanStop) => {
    if (at.id !== stop.id) void navigate(`/trips/${id}/stops/${at.id}`);
  };
  // Another stop pressed: said beside the map, its days opened from there; this one, nothing.
  const glance = (at: PlanStop) => {
    if (at.id !== stop.id) pick(`stop:${at.id}`);
  };
  const otherStop = ready.stops.find((one) => `stop:${one.id}` === picked) ?? null;
  // The stop the way in comes from.
  const before = ready.stops[ready.stops.indexOf(stop) - 1];
  // The day looked at, from its midnight to the next, at the stop's clock.
  const focusDay: [number, number] = [
    Date.parse(instantOf(shown, '00:00', stop.zone)),
    Date.parse(instantOf(plusDays(shown, 1), '00:00', stop.zone)),
  ];
  // The day's things on the timeline, each from its start to its end — an hour when none is
  // said, past midnight when it ends before it starts — and the walk before each, as long as it
  // takes.
  const locale = lang === 'et' ? 'et-EE' : 'en-GB';
  // A day as a booking site says it: `Mon, 8 March`.
  const weekdaySaid = (iso: string) =>
    new Intl.DateTimeFormat(locale, {
      weekday: 'short',
      day: 'numeric',
      month: 'long',
      timeZone: 'UTC',
    }).format(Date.parse(`${iso}T00:00:00Z`));
  // A thing's hours and how long they are: `11:00–13:00 · 2 h`; past midnight, the next day's.
  const hoursSaid = (starts: string, ends: string | null) => {
    if (ends === null) return starts;
    const at = (clock: string) => Number(clock.slice(0, 2)) * 60 + Number(clock.slice(3, 5));
    const minutes = (at(ends) - at(starts) + DAY_MINUTES) % DAY_MINUTES;
    return `${starts}–${ends} · ${minutesSaid(minutes)}`;
  };
  const longDay = new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  });
  // A way in or out by what and from where to where: "Flight Shanghai → Tokyo".
  const routeOf = (item: Card & { kind: 'arrive' | 'leave' }) => {
    const [from, to] =
      item.kind === 'arrive' ? [item.other, stop.city] : [stop.city, item.other];
    return `${t(`mode.${item.mode}`)} ${from} → ${to}`;
  };
  const what = (item: Card) => {
    if (item.kind === 'thing') return item.item.name;
    if (item.kind === 'stay') return item.booking.name;
    // A way in or out by where it lands or leaves from, the airport, the station; with none
    // said, by what and from where to where.
    return item.place?.name ?? routeOf(item);
  };
  const detail = (item: Card) => {
    if (item.kind === 'stay') {
      // Its night of all: the one the day ends in, the day it is checked into, or out of.
      if (item.edge !== 'morning') {
        const word = t(item.edge === 'in' ? 'city.checkIn' : 'city.checkOut');
        return item.at ? `${word} ${item.at}` : word;
      }
      return t('city.nightOf', { night: item.night, nights: item.booking.nights });
    }
    if (item.kind === 'thing') {
      // In the money it is paid in, as its ticket says it: ¥3,100.
      const priced = item.item.paid ?? item.item.price;
      const parts = [
        item.item.starts && item.item.ends
          ? `${item.item.starts}–${item.item.ends}`
          : item.item.starts,
        priced ? formatMoney(priced, lang) : null,
        item.item.booked ? t('city.booked') : null,
        item.place?.name ?? null,
      ];
      return parts.filter((part) => part !== null).join(' · ');
    }
    // By what, from where to where, when it lands or leaves, at which terminal: "Flight
    // Shanghai → Tokyo · lands 14:20 · terminal 3".
    const arrives = item.kind === 'arrive';
    return [
      // Named by its airport above: by what and from where to where, here.
      item.place ? routeOf(item) : null,
      item.at ? t(arrives ? 'city.lands' : 'city.leaves', { time: item.at }) : null,
      item.terminal ? t('city.terminal', { terminal: item.terminal }) : null,
    ]
      .filter(Boolean)
      .join(' · ');
  };

  // The day in turn, as the list walks it: the way in when it lands, each thing from its start
  // to its end, each way as soon as what came before is done — red when it would end after what
  // it goes to starts, drawn in what room there is — the room when it is checked into or out
  // of, or got back to, and the way on as it leaves. A way in or on with no time said is where
  // the trip's timeline has the stop begin and end.
  const EDGE = 20 * 60_000;
  const [begins, ends] = layoutOf(ready).blocks.reduce<[number, number]>(
    (found, block) => (block.stop.id === stop.id ? [block.from, block.to] : found),
    [0, 0],
  );
  const turnOf = (on: string, its: DayItem[]) => {
    const at = (clock: string) => Date.parse(instantOf(on, clock, stop.zone));
    return its.reduce<{ blocks: AgendaBlock[]; ended: number | null; lates: string[] }>(
      (done, item) => {
        const move = moveTo(item, on);
        const timed = item.kind === 'back' ? null : item.at;
        const start =
          timed !== null
            ? at(timed)
            : item.kind === 'arrive'
              ? begins
              : item.kind === 'leave'
                ? ends
                : null;
        const room = item.kind === 'stay' || item.kind === 'back';
        const label =
          item.kind === 'back' || item.kind === 'stay' ? item.booking.name : what(item);
        // What it is at its own time: a thing from its start to its end, the way in once off, the
        // way on as it leaves, a room as it is checked into or out of.
        const own: AgendaBlock | null =
          start === null
            ? null
            : item.kind === 'thing'
              ? {
                  key: item.key,
                  label: item.item.name,
                  from: start,
                  to: (() => {
                    const ends = item.item.ends ? at(item.item.ends) : start + HOUR;
                    return ends <= start ? ends + DAY : ends;
                  })(),
                  kind: 'thing',
                }
              : {
                  key: item.key,
                  label,
                  from: item.kind === 'leave' ? start - EDGE : start,
                  to: item.kind === 'leave' ? start : start + EDGE,
                  kind: room ? 'room' : 'thing',
                };
        // The way to it, as long as it takes when that is known.
        const timedMove =
          move && move.minutes !== null ? { mode: move.mode, minutes: move.minutes } : null;
        const placed: AgendaBlock[] = [];
        let { lates } = done;
        let ended = done.ended;
        if (timedMove) {
          const span = timedMove.minutes * 60_000;
          const way = (from: number, to: number, late: boolean): AgendaBlock => ({
            key: `${item.key}-way`,
            label: minutesSaid(timedMove.minutes),
            from,
            to,
            kind: 'move',
            mode: timedMove.mode,
            pick: `way:${item.key}`,
            late,
          });
          if (own) {
            const leaves = own.from - span;
            const free = ended ?? leaves;
            if (own.kind === 'room') {
              // To a room: as soon as what came before is done, the bags left. Its check-in is
              // when it can be had from, not a time to be there by: never late.
              placed.push(way(free, free + span, false));
            } else {
              // To a thing: set out just in time, the time free spent where one was. Not made in
              // time: only in the room there is, red.
              const starts = Math.max(leaves, free);
              const late = free > leaves;
              if (late) lates = [...lates, item.key];
              if (starts < own.from) {
                placed.push(way(starts, Math.min(starts + span, own.from), late));
              }
            }
          } else if (ended !== null) {
            // No time of its own: as soon as what came before is done.
            placed.push(way(ended, ended + span, false));
            ended += span;
          }
        }
        // A room with no time of its own, got back to or checked into, once there.
        const after: AgendaBlock | null =
          own === null && ended !== null && (item.kind === 'stay' || item.kind === 'back')
            ? {
                key: item.key,
                label,
                from: ended,
                to: ended + EDGE,
                kind: 'room',
                pick: item.kind === 'back' ? item.morning : item.key,
              }
            : null;
        // A room is had from when it is got to, before its check-in or after: the way there
        // done, else what came before.
        const got = placed.at(-1)?.to ?? ended;
        if (own) {
          placed.push(
            own.kind === 'room' && got !== null
              ? { ...own, from: got, to: Math.max(own.to, got + EDGE) }
              : own,
          );
        }
        if (after) placed.push(after);
        return {
          blocks: [...done.blocks, ...placed],
          // What comes after starts once the latest yet is done, a thing put before one earlier.
          ended: Math.max(ended ?? 0, placed.at(-1)?.to ?? 0) || null,
          lates,
        };
      },
      { blocks: [], ended: null, lates: [] },
    );
  };
  // Every day of the stop on the timeline, each block by its day: pressed, that day is looked at.
  const drawn = held.flatMap(({ on, items: its }) =>
    turnOf(on, its).blocks.map((block) => ({
      ...block,
      key: `${on} ${block.key}`,
      pick: `${on} ${block.pick ?? block.key}`,
    })),
  );
  // A room is slept in until it is next set out from: the night, the morning, to the day's
  // first way.
  const blocks = drawn.map((block, index) => {
    if (block.kind !== 'room') return block;
    const next = drawn.slice(index + 1).find((later) => later.from >= block.to);
    return next ? { ...block, to: next.from } : block;
  });
  const { lates } = turnOf(shown, items);
  const poiName = poi?.name ?? null;
  const dayCosts = costs.filter((expense) => expense.day === shown);
  const summary = t('city.summary', {
    count: items.filter((item) => item.kind === 'thing').length,
    walk: distanceSaid(walkedMetres, lang),
    money: formatMoney(
      {
        currency: ready.trip.currency,
        minor:
          stop.agenda
            .filter((one) => one.day === shown)
            .reduce((sum, one) => sum + (one.price?.minor ?? 0), 0) +
          dayCosts.reduce((sum, one) => sum + one.amount.minor, 0),
      },
      lang,
    ),
  });
  const covered = window.innerWidth > 640;
  const title = t('city.title', {
    city: stop.city,
    dates: dayRange(stop.start, stop.end, lang),
  });
  const dayTitle = longDay.format(Date.parse(`${shown}T00:00:00Z`));

  return (
    <DockFrame canvas={setCanvas}>
      <h1 className="visually-hidden">
        {title} · {countryName(stop.country, lang)}
      </h1>
      <section
        className="panel plan-map"
        aria-labelledby="city-map-title"
        ref={(node) => {
          if (node) mapBox.current = node;
        }}
      >
        <h2 id="city-map-title">{t('map.title')}</h2>
        <MapPanel
          data={data}
          label={t('map.label', { places: data.points.map((p) => p.label).join(' → ') })}
          // As the plan's map: the wheel zooms it at once where the widgets cover it.
          padding={covered ? COVERED : undefined}
          cooperative={!covered}
          closest={16}
          thin
          // Fitted once; after, a press changes the view only for what is out of sight — the
          // day's places when another day is looked at, a place picked, a way picked.
          keepView
          show={data.points}
          // The box of a place pressed goes with it as the map moves; gone with it out of sight.
          follow={
            poi && {
              lat: poi.lat,
              lon: poi.lon,
              onMove: (at) => {
                const box = mapBox.current;
                const out =
                  at.x < 0 || at.y < 0 || at.x > box.clientWidth || at.y > box.clientHeight;
                setPoi((was) => (was === null || out ? null : { ...was, ...at }));
              },
            }
          }
          focus={chosen?.place ?? null}
          // A way stands out only when it is picked itself: a place picked is a place, not the
          // way to it.
          way={
            shownStep
              ? shownPart
                ? `${shownStep.to.key}#${String(partPicked)}`
                : shownStep.to.key
              : null
          }
          fit={
            shownPart
              ? [shownPart.segment.from, shownPart.segment.to]
              : shownRoute
                ? [shownRoute.step.start, ...shownRoute.parts.map(({ segment }) => segment.to)]
                : // A day with nothing on the map: the town itself, not the world.
                  data.points.length === 0 && stop.place
                  ? [stop.place]
                  : null
          }
          // A way pressed on the map picks the thing it goes to, a place the thing it is.
          onLine={(key) => {
            pick(`way:${key}`);
          }}
          onPoint={pick}
          // A place pressed is there to add to the day: on a trip read only, nothing is.
          onPoi={
            locked
              ? undefined
              : (pressed) => {
                  asked.current += 1;
                  const ask = asked.current;
                  setPoi({ ...pressed, room: mapBox.current.clientWidth, near: null, ask });
                  // Its answer kept only while the same press is still open.
                  const answered = (near: NearThing[]) => {
                    setPoi((was) => (was?.ask === ask ? { ...was, near } : was));
                  };
                  getNearby(pressed).then(
                    (found) => {
                      answered(found.near);
                    },
                    () => {
                      answered([]);
                    },
                  );
                }
          }
        />
        {poi !== null && (
          <section
            // Kept in the map: under the point near its top, short of the buttons at its right.
            className={`mapadd-pop city-poi${poi.y < POI_ROOM ? ' city-poi-below' : ''}`}
            style={poiPlace(poi)}
            aria-labelledby="city-poi-name"
          >
            <div className="mapadd-pop-head">
              <span className="mapadd-caps">{weekdaySaid(shown)}</span>
              <b id="city-poi-name">{poi.name ?? t('city.poiHere')}</b>
            </div>
            {/* What is near, from OpenStreetMap: each added as the place pressed is. */}
            {poi.near === null ? (
              <p className="mapadd-small quiet mapadd-pop-wait">{t('myPlaces.asking')}</p>
            ) : poi.near.length > 0 ? (
              <ul className="mapadd-near" aria-label={t('mapAdd.near')}>
                {poi.near.map((near) => (
                  <li key={`${near.name}-${String(near.metres)}`}>
                    <span>
                      {near.emoji} {near.name}
                    </span>
                    <span className="mapadd-small quiet mono">{near.metres} m</span>
                    <button
                      type="button"
                      className="link-button"
                      aria-label={`${t('mapAdd.add')}: ${near.name}`}
                      onClick={() => {
                        addThing(near.name, near);
                      }}
                    >
                      {t('mapAdd.add')}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              poiName === null && (
                <p className="mapadd-small quiet mapadd-pop-wait">{t('city.poiNone')}</p>
              )
            )}
            <div className="mapadd-pop-foot">
              {/* Nothing named here: the spot itself, named in the dialog. */}
              {poiName === null && poi.near !== null && (
                <button
                  type="button"
                  className="button button-small"
                  onClick={() => {
                    setPoi(null);
                    setEditing({ item: null, at: gpsSpot(poi.lat, poi.lon) });
                  }}
                >
                  {t('city.poiPick')}
                </button>
              )}
              {poiName !== null && (
                <button
                  type="button"
                  className="button button-small"
                  onClick={() => {
                    addThing(poiName, poi);
                  }}
                >
                  {t('city.poiAdd')}
                </button>
              )}
              <button
                type="button"
                className="button button-quiet button-small"
                onClick={() => {
                  setPoi(null);
                }}
              >
                {t('mapAdd.close')}
              </button>
            </div>
          </section>
        )}
      </section>
      {/* A place looked for by its name or address, near the town first: added to the day. On
          the map, as the plan's search is. */}
      {!locked && (
        <div className="plan-search city-search">
          <SpotField
            search
            label={t('city.search')}
            near={stop.place}
            spot={null}
            onPick={(spot) => {
              setEditing({ item: null, at: spot });
            }}
          />
        </div>
      )}
      {widgets['city-days'] && (
        <Widget
          id="city-days"
          title={t('city.days', { city: stop.city })}
          place={LEFT}
          width={320}
          area={canvas}
          onClose={() => {
            show('city-days', false);
          }}
          actions={
            <>
              {/* Back to the whole trip, as a stop's widget goes back to the route. */}
              <Link
                className="icon-button icon-button-small"
                to={`/trips/${id}`}
                aria-label={t('city.back')}
                title={t('city.back')}
              >
                ‹
              </Link>
            </>
          }
        >
          <div className="city-plan">
            <DayStep
              label={t('city.dayPick')}
              first={stop.start}
              last={stop.end}
              value={shown}
              note={planned}
              onChange={(one) => {
                setDay(one);
                setPicked(null);
              }}
            />
            {movable >= 2 && !looked && !locked && (
              <button
                type="button"
                className="link-button day-order-ask"
                onClick={() => {
                  setOrdering({
                    day: shown,
                    order: shortest(dayStart, spots, dayEnd).map((spot) => spot.key),
                    pace: 'walk',
                  });
                }}
              >
                {t('dayOrder.ask')}
              </button>
            )}
            {looked && ordering && (
              <DayOrderPanel
                now={lengthOf(dayStart, spots, dayEnd)}
                best={lengthOf(
                  dayStart,
                  looked.flatMap((key) => spots.filter((spot) => spot.key === key)),
                  dayEnd,
                )}
                pace={ordering.pace}
                onPace={(pace) => {
                  setOrdering({ ...ordering, pace });
                }}
                onCancel={() => {
                  setOrdering(null);
                }}
                onApply={() => {
                  setOrdering(null);
                  change(orderAgenda(ready.trip.id, looked));
                }}
              />
            )}
            <ol className="city-items" aria-label={t('city.plan')}>
              {items.map((item) => {
                const move = moveTo(item);
                // Back to the room at the day's end: the way there, said with where it goes.
                // Back to the room at the day's end: the way there, then the room as at its
                // start, picked as that one is.
                if (item.kind === 'back') {
                  return (
                    <li key={item.key}>
                      {move && wayLine(item, move)}
                      <button
                        type="button"
                        className="city-item city-item-stay"
                        aria-pressed={item.morning === picked}
                        onClick={() => {
                          setPicked(item.morning === picked ? null : item.morning);
                          show('city-picked', true);
                        }}
                      >
                        <span className="city-pin" aria-hidden="true">
                          <ModeIcon mode={item.booking.lodging ?? 'stay'} size={12} />
                        </span>
                        <span className="city-what">
                          {item.booking.name}
                          <small>
                            {t('city.nightOf', {
                              night: item.night,
                              nights: item.booking.nights,
                            })}
                          </small>
                        </span>
                      </button>
                    </li>
                  );
                }
                // A thing done is dragged into its place in the day, or moved by Alt and an
                // arrow: the way the day is walked.
                const thing = item.kind === 'thing';
                return (
                  <li
                    key={item.key}
                    className={[
                      dragged === item.key ? 'city-dragging' : '',
                      over === item.key && dragged !== item.key ? 'city-over' : '',
                    ].join(' ')}
                    onDragOver={(event) => {
                      if (!thing || dragged === null) return;
                      event.preventDefault();
                      setOver(item.key);
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      const from = order.indexOf(dragged ?? '');
                      setDragged(null);
                      setOver(null);
                      moveThing(from, order.indexOf(item.key));
                    }}
                  >
                    {move && wayLine(item, move)}
                    {/* The card alone is held: the way to it stays where the day says. */}
                    <button
                      type="button"
                      draggable={thing && !locked}
                      onDragStart={(event) => {
                        event.dataTransfer.effectAllowed = 'move';
                        event.dataTransfer.setData('text/plain', item.key);
                        setDragged(item.key);
                      }}
                      onDragEnd={() => {
                        setDragged(null);
                        setOver(null);
                      }}
                      className={`city-item city-item-${item.kind}`}
                      aria-pressed={item.key === picked}
                      aria-keyshortcuts={thing ? 'Alt+ArrowUp Alt+ArrowDown' : undefined}
                      onClick={() => {
                        setPicked(item.key === picked ? null : item.key);
                        show('city-picked', true);
                      }}
                      onKeyDown={(event) => {
                        const by = { ArrowUp: -1, ArrowDown: 1 }[event.key];
                        if (!thing || !event.altKey || by === undefined) return;
                        event.preventDefault();
                        const at = order.indexOf(item.key);
                        moveThing(at, at + by);
                      }}
                    >
                      <span className="city-pin" aria-hidden="true">
                        {item.kind === 'thing' ? (
                          item.number
                        ) : (
                          <ModeIcon
                            mode={
                              item.kind === 'stay'
                                ? (item.booking.lodging ?? 'stay')
                                : item.mode
                            }
                            size={12}
                          />
                        )}
                      </span>
                      <span className="city-what">
                        {what(item)}
                        <small>{detail(item)}</small>
                        {looked && item.kind === 'thing' && (
                          <span className="day-order-mark">
                            {item.at !== null
                              ? t('dayOrder.fixed')
                              : item.number !== looked.indexOf(item.key) + 1 &&
                                t('dayOrder.was', { n: item.number })}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
            {!locked && <WaitingPlaces trip={id} stop={stop} plan={ready} onChange={change} />}
          </div>
        </Widget>
      )}
      {widgets['city-picked'] && (
        <Widget
          id="city-picked"
          title={
            shownStep
              ? t('way.widget', { from: nameOf(shownStep.from), to: nameOf(shownStep.to) })
              : otherStop
                ? otherStop.city
                : chosen
                  ? t(`city.titleOf.${chosen.kind}`, { name: what(chosen) })
                  : dayTitle
          }
          place={RIGHT}
          width={300}
          area={canvas}
          onClose={() => {
            show('city-picked', false);
            setPicked(null);
          }}
          actions={
            // A thing changed, or the thing a way goes to: how it is got to said there; the way
            // back to the room, said on its own.
            locked ? undefined : shownStep && leg ? (
              <button
                type="button"
                className="icon-button icon-button-small"
                aria-label={t('city.change')}
                title={t('city.change')}
                onClick={() => {
                  setDayWayOpen({
                    leg,
                    from: nameOf(shownStep.from),
                    to: nameOf(shownStep.to),
                  });
                }}
              >
                <PencilIcon />
              </button>
            ) : editable ? (
              <button
                type="button"
                className="icon-button icon-button-small"
                aria-label={t('city.change')}
                title={t('city.change')}
                onClick={() => {
                  setEditing({ item: editable.id });
                }}
              >
                <PencilIcon />
              </button>
            ) : undefined
          }
        >
          <div className="city-picked" aria-live="polite">
            {otherStop ? (
              <>
                {/* Another stop of the trip: when and how long, its days opened from here. */}
                <p className="quiet">
                  {dayRange(otherStop.start, otherStop.end, lang)} ·{' '}
                  {t('trips.days', { count: otherStop.days })}
                </p>
                <Link to={`/trips/${id}/stops/${otherStop.id}`}>
                  {t('city.openDays', { city: otherStop.city })}
                </Link>
              </>
            ) : shownRoute ? (
              <>
                <p className="city-box-way">
                  {moveOf(shownRoute).modes.map((mode, at) => (
                    <ModeIcon key={`${String(at)} ${mode}`} mode={mode} size={14} />
                  ))}
                  {moveOf(shownRoute)
                    .modes.map((mode) => t(`city.by.${mode}`))
                    .join(' → ')}{' '}
                  · {moveOf(shownRoute).said}
                </p>
                {shownRoute.parts.map(({ segment, found }, at) => (
                  <div key={`${String(at)} ${segment.by}`} className="city-part">
                    {/* With changes: each part, by what, to where, how long. */}
                    {/* Pressed, that part alone on the map; again, the whole way. */}
                    {shownRoute.parts.length > 1 && (
                      <button
                        type="button"
                        className="city-part-head"
                        aria-pressed={shownPart === shownRoute.parts[at]}
                        onClick={() => {
                          const whole = `way:${shownRoute.step.to.key}`;
                          const part = `${whole}#${String(at)}`;
                          setPicked(picked === part ? whole : part);
                        }}
                      >
                        <ModeIcon mode={segment.by} size={12} />
                        <span>
                          {t(`city.by.${segment.by}`)} →{' '}
                          {/* Ridden: to the station it gets off at, not the point said. */}
                          {found.ride?.legs.findLast((one) => one.mode !== 'walk')?.to ??
                            segment.name}
                        </span>
                        {found.minutes !== null && (
                          <span className="quiet mono">{minutesSaid(found.minutes)}</span>
                        )}
                      </button>
                    )}
                    {found.ride && <RideLegs ride={found.ride} />}
                  </div>
                ))}
                {lates.includes(shownRoute.step.to.key) && (
                  <p className="city-late-said">{t('city.lateLong')}</p>
                )}
                {/* Each way there to take instead, as the CityRides mockup offers them. */}
                <RideChoices
                  key={`${shown} ${shownRoute.step.to.key}`}
                  from={shownRoute.step.start}
                  to={shownRoute.step.end}
                  fromName={nameOf(shownRoute.step.from)}
                  leaves={leavesAt(shownRoute.step, shown)}
                  live={shown === townToday}
                  town={{ country: stop.country, city: stop.city }}
                  said={shownRoute.first.by}
                  parkNear={cameByCar ? stop.place : null}
                  onPick={
                    locked
                      ? undefined
                      : (by, minutes) => {
                          const way = { by, byMinutes: minutes, parts: [] };
                          change(keepWay(ready, stop.id, shown, shownRoute.step, way));
                        }
                  }
                  onStations={setDocks}
                  onMe={setMe}
                />
              </>
            ) : (
              <>
                {!chosen && <p className="quiet">{summary}</p>}
                {/* What was paid this day, from the trip's shared costs: what, who paid, in the
                    money it was paid in and the trip's. */}
                {!chosen && dayCosts.length > 0 && (
                  <section className="city-costs" aria-label={t('city.costs')}>
                    <h3>{t('city.costs')}</h3>
                    <ul>
                      {dayCosts.map((expense) => (
                        <li key={expense.id}>
                          <span>
                            {expense.item}
                            <small className="quiet">
                              {t('city.costPaid', { name: expense.payer })}
                            </small>
                          </span>
                          <span className="mono">
                            {formatMoney(expense.paid, lang)}
                            {expense.paid.currency !== expense.amount.currency && (
                              <small className="quiet">
                                {formatMoney(expense.amount, lang)}
                              </small>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <Link to={`/trips/${id}/group`}>{t('city.costsAll')}</Link>
                  </section>
                )}
                {/* A thing picked, as the mockup's card says it: its day, its hours and how
                    long, its price in its own money and whether it is booked, where it is. */}
                {chosen?.kind === 'thing' && chosen.item.place && (
                  // Its picture by its own name where it is: Sensō-ji, not the Asakusa it is in.
                  <PlacePhoto place={{ ...chosen.item.place, name: chosen.item.name }} />
                )}
                {chosen?.kind === 'thing' && (
                  <dl className="way-facts">
                    <div>
                      <dt>{t('city.facts.day')}</dt>
                      <dd>{weekdaySaid(chosen.item.day)}</dd>
                    </div>
                    {chosen.item.starts && (
                      <div>
                        <dt>{t('city.facts.time')}</dt>
                        <dd>{hoursSaid(chosen.item.starts, chosen.item.ends)}</dd>
                      </div>
                    )}
                    {chosen.item.price && (
                      <div>
                        <dt>{t('city.facts.price')}</dt>
                        <dd>{priceSaid(chosen.item.price, chosen.item.paid, lang)}</dd>
                      </div>
                    )}
                    <div>
                      <dt>{t('city.facts.booking')}</dt>
                      <dd>
                        {/* A status as a booking site shows one: a coloured badge. */}
                        <span
                          className={`status-badge status-badge-${chosen.item.booked ? 'booked' : 'open'}`}
                        >
                          {t(chosen.item.booked ? 'city.facts.booked' : 'city.facts.notBooked')}
                        </span>
                      </dd>
                    </div>
                    {chosen.item.place && (
                      <div className="way-fact-wide">
                        <dt>{t('city.facts.place')}</dt>
                        <dd>{chosen.item.place.name}</dd>
                      </div>
                    )}
                  </dl>
                )}
                {/* Its photos and PDFs, each pressed to look at it in place. */}
                {chosen?.kind === 'thing' && chosen.item.files.length > 0 && (
                  <ul className="thing-file-list" aria-label={t('city.thing.files')}>
                    {chosen.item.files.map((file) => (
                      <li key={file.id}>
                        <button
                          type="button"
                          className="button button-small"
                          onClick={() => {
                            setLooking(file);
                          }}
                        >
                          {file.type === 'application/pdf' ? 'PDF' : t('city.photo')} ·{' '}
                          {file.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {/* A way in or out of the town, all that is known of it, as the plan shows it:
                    its flights, when it leaves and lands at each end, its terminal, its seats. */}
                {chosen?.kind === 'arrive' && before && (
                  <WayDetail plan={ready} stop={before} onPlan={locked ? undefined : saved} />
                )}
                {chosen?.kind === 'leave' && (
                  <WayDetail plan={ready} stop={stop} onPlan={locked ? undefined : saved} />
                )}
              </>
            )}
            {/* A room, as a booking site shows it: what kind of place and which night, when it
                is had and left side by side, what it costs a night, where it is. */}
            {chosen?.kind === 'stay' && (
              <div className="stay-card">
                <p className="stay-kind">
                  <ModeIcon mode={chosen.booking.lodging ?? 'stay'} size={14} />
                  {chosen.booking.lodging
                    ? t(`lodging.${chosen.booking.lodging}`)
                    : t('bookingForm.lodgingAny')}{' '}
                  · {t('city.nightOf', { night: chosen.night, nights: chosen.booking.nights })}
                </p>
                {chosen.booking.day !== null && (
                  <div className="stay-dates">
                    <div>
                      <span>{t('city.checkInLabel')}</span>
                      <strong>{weekdaySaid(chosen.booking.day)}</strong>
                      {chosen.booking.checkIn && (
                        <small>{t('city.fromTime', { time: chosen.booking.checkIn })}</small>
                      )}
                    </div>
                    <div>
                      <span>{t('city.checkOutLabel')}</span>
                      <strong>
                        {weekdaySaid(plusDays(chosen.booking.day, chosen.booking.nights))}
                      </strong>
                      {chosen.booking.checkOut && (
                        <small>{t('city.untilTime', { time: chosen.booking.checkOut })}</small>
                      )}
                    </div>
                  </div>
                )}
                <p className="stay-price">
                  <strong>{priceSaid(chosen.booking.price, chosen.booking.paid, lang)}</strong>
                  <span className="quiet">
                    {t('city.nights', { count: chosen.booking.nights })}
                    {chosen.booking.nights > 0 &&
                      ` · ${t('city.perNight', {
                        price: formatMoney(
                          {
                            ...chosen.booking.price,
                            minor: Math.round(
                              chosen.booking.price.minor / chosen.booking.nights,
                            ),
                          },
                          lang,
                        ),
                      })}`}
                  </span>
                </p>
                {chosen.booking.place && (
                  <p className="stay-address">
                    <PinIcon />
                    {chosen.booking.place.name}
                  </p>
                )}
                {chosen.booking.reference && (
                  <p className="quiet">
                    {t('city.bookingRef', { ref: chosen.booking.reference })}
                  </p>
                )}
                {chosen.booking.link && (
                  <a href={chosen.booking.link} target="_blank" rel="noreferrer">
                    {t('city.openBooking')}
                  </a>
                )}
              </div>
            )}
            {chosen?.kind === 'thing' && chosen.item.link && (
              <a href={chosen.item.link} target="_blank" rel="noreferrer">
                {chosen.item.link.replace(/^https?:\/\//, '')} ↗
              </a>
            )}
            {chosen && hereOf(chosen) && (
              <div className="city-box">
                <strong>{t('city.there')}</strong>
                <span className="quiet">
                  {t('city.fromPlace', { name: hereOf(chosen)?.from })}
                </span>
                <span className="city-box-way">
                  <ModeIcon mode={hereOf(chosen)?.move.mode} size={14} />
                  {hereOf(chosen)?.move.said}
                </span>
              </div>
            )}
            {/* How one goes on from it: to the next thing, or the room. */}
            {chosen && onFrom(chosen) && (
              <div className="city-box">
                <strong>{t('city.onFrom', { name: onFrom(chosen)?.name })}</strong>
                <span className="city-box-way">
                  <ModeIcon mode={onFrom(chosen)?.move.mode} size={14} />
                  {onFrom(chosen)?.move.said}
                </span>
              </div>
            )}
          </div>
        </Widget>
      )}
      {widgets['city-hours'] && (
        <Widget
          id="city-hours"
          title={t('timeline.title')}
          place={BOTTOM}
          width={between}
          area={canvas}
          onClose={() => {
            show('city-hours', false);
          }}
        >
          <Timeline
            key={stop.id}
            plan={ready}
            selected={stop.id}
            way={null}
            agenda={blocks}
            weather={readyWeather(sky)}
            alone
            within={[
              Date.parse(instantOf(stop.start, '00:00', stop.zone)),
              Date.parse(instantOf(plusDays(stop.end, 1), '00:00', stop.zone)),
            ]}
            agendaPicked={picked === null ? null : `${shown} ${picked.replace(/#.*/, '')}`}
            onAgenda={(picks) => {
              const space = picks.indexOf(' ');
              setDay(picks.slice(0, space));
              pick(picks.slice(space + 1));
            }}
            focus={focusDay}
            zoomKey="trippath-city-zoom"
            startZoom="hour"
            onPick={glance}
            onOpen={go}
            onDays={(at, count) => {
              change(updateStop(ready.trip.id, at.id, withDays(at, count)));
            }}
            onMove={(from, to) => {
              const ids = ready.stops.map((one) => one.id);
              change(
                reorderStops(
                  ready.trip.id,
                  ids.toSpliced(from, 1).toSpliced(to, 0, ...ids.slice(from, from + 1)),
                ),
              );
            }}
            onShift={(at, departureAt, arrivalAt) => {
              change(updateStop(ready.trip.id, at.id, withWayAt(at, departureAt, arrivalAt)));
            }}
            onAddAfter={() => {
              void navigate(`/trips/${id}`);
            }}
            onWay={(at) => {
              // This stop's way on, or the way into it from the one before: said in the day.
              if (at.id === stop.id) setPicked('leave');
              else if (
                ready.stops[ready.stops.findIndex((one) => one.id === at.id) + 1]?.id ===
                stop.id
              )
                setPicked('arrive');
              else glance(at);
            }}
          />
          {failed && (
            <p className="form-failed" role="alert">
              {t('plan.failed')}
            </p>
          )}
        </Widget>
      )}
      <WidgetDock
        items={[
          { id: 'city-days', title: t('city.days', { city: stop.city }) },
          { id: 'city-picked', title: t('city.picked') },
          { id: 'city-hours', title: t('timeline.title') },
        ]}
        shown={widgets}
        onShow={show}
      />
      {looking && (
        <FileView
          trip={ready.trip.id}
          file={looking}
          onClose={() => {
            setLooking(null);
          }}
        />
      )}
      {editing && (
        <AgendaDialog
          plan={ready}
          stop={stop}
          item={editingItem}
          day={shown}
          at={editing.at}
          onClose={() => {
            setEditing(null);
          }}
          onSaved={saved}
        />
      )}
      {dayWayOpen && (
        <DayWayDialog
          plan={ready}
          stop={stop}
          day={shown}
          leg={dayWayOpen.leg}
          from={dayWayOpen.from}
          to={dayWayOpen.to}
          onClose={() => {
            setDayWayOpen(null);
          }}
          onSaved={saved}
        />
      )}
    </DockFrame>
  );
}
